import type { LevelDefinition } from "@/domain";
import {
  kebabCaseIdentifierSchema,
  levelDefinitionSchema,
  levelIdSchema
} from "@/domain/schemas";

import type {
  PlayLevelRegistry,
  PlayRendererRegistration,
  PlayRendererRegistry
} from "./contracts";

export type PlayLevelResolution =
  | {
      readonly status: "resolved";
      readonly level: LevelDefinition;
    }
  | {
      readonly status: "error";
      readonly code:
        | "invalid-level-id"
        | "missing-level"
        | "duplicate-level"
        | "invalid-level";
    };

export type PlayRendererResolution =
  | {
      readonly status: "resolved";
      readonly registration: PlayRendererRegistration;
    }
  | {
      readonly status: "error";
      readonly code: "missing-renderer" | "duplicate-renderer";
    };

function readLevelId(value: unknown): string | null {
  if (
    typeof value !== "object" ||
    value === null ||
    !("id" in value) ||
    typeof value.id !== "string"
  ) {
    return null;
  }

  return value.id;
}

export function resolvePlayLevel(
  registry: PlayLevelRegistry,
  requestedLevelId: string | null | undefined
): PlayLevelResolution {
  const idResult = levelIdSchema.safeParse(requestedLevelId);
  if (!idResult.success) {
    return { status: "error", code: "invalid-level-id" };
  }

  const candidates = registry.filter(
    (candidate) => readLevelId(candidate) === idResult.data
  );

  if (candidates.length === 0) {
    return { status: "error", code: "missing-level" };
  }

  if (candidates.length > 1) {
    return { status: "error", code: "duplicate-level" };
  }

  const levelResult = levelDefinitionSchema.safeParse(candidates[0]);
  if (!levelResult.success) {
    return { status: "error", code: "invalid-level" };
  }

  return { status: "resolved", level: levelResult.data };
}

export function resolvePlayRenderer(
  registry: PlayRendererRegistry,
  activityType: string
): PlayRendererResolution {
  if (!kebabCaseIdentifierSchema.safeParse(activityType).success) {
    return { status: "error", code: "missing-renderer" };
  }

  const candidates = registry.filter(
    (registration) => registration.activityType === activityType
  );

  if (candidates.length === 0) {
    return { status: "error", code: "missing-renderer" };
  }

  if (candidates.length > 1) {
    return { status: "error", code: "duplicate-renderer" };
  }

  const registration = candidates[0];
  if (registration === undefined) {
    return { status: "error", code: "missing-renderer" };
  }

  return { status: "resolved", registration };
}
